import { ref } from 'vue';
import { defineStore } from 'pinia';
import Common from '@/libs/common';
import type { FavoriteGroup, Group } from '@/@types/group';

// Not under `favorites.`: that prefix is the per-network namespace of lines and stops.
export const FAVORITE_GROUPS_KEY = 'info.favoriteGroups';

export const MAX_FAVORITE_GROUPS = 5;

type StoredFavoriteGroups = { v: 1; groups: FavoriteGroup[] };
type FavoriteGroupSource = Pick<Group, 'group_id' | 'name' | 'color' | 'logo_url'>;

function toFavorite(
  group: FavoriteGroupSource,
  notificationsEnabled: boolean,
  lastUsedAt?: number,
): FavoriteGroup {
  return {
    groupId: group.group_id,
    name: group.name,
    color: group.color,
    logoUrl: group.logo_url || undefined,
    notificationsEnabled,
    lastUsedAt,
  };
}

/**
 * Drops the least recently used favorites past the limit, never `keptGroupId`. Its notifications go
 * with it: the topics follow the list. Favorites never used since `lastUsedAt` exists go first, the
 * oldest added first.
 */
function evictLeastRecentlyUsed(favorites: FavoriteGroup[], keptGroupId?: string): FavoriteGroup[] {
  const kept = [...favorites];
  while (kept.length > MAX_FAVORITE_GROUPS) {
    const candidates = kept.filter(favorite => favorite.groupId !== keptGroupId);
    const evicted = candidates.reduce((oldest, favorite) =>
      (favorite.lastUsedAt ?? 0) < (oldest.lastUsedAt ?? 0) ? favorite : oldest,
    );
    kept.splice(kept.indexOf(evicted), 1);
  }
  return kept;
}

function isFavoriteGroup(value: unknown): value is FavoriteGroup {
  const favorite = value as Partial<FavoriteGroup> | null;
  return (
    typeof favorite?.groupId === 'string' &&
    favorite.groupId !== '' &&
    typeof favorite.name === 'string' &&
    (typeof favorite.color === 'string' || favorite.color === null) &&
    typeof favorite.notificationsEnabled === 'boolean' &&
    (favorite.lastUsedAt === undefined || typeof favorite.lastUsedAt === 'number')
  );
}

function loadFavoriteGroups(): FavoriteGroup[] {
  const stored: StoredFavoriteGroups | null = Common.safeLSParse(FAVORITE_GROUPS_KEY);
  const current: Group | null = Common.safeLSParse('group');
  if (Array.isArray(stored?.groups)) {
    // Lists saved before the limit existed can be longer: the network in use stays.
    const favorites = stored.groups.filter(isFavoriteGroup);
    const trimmed = evictLeastRecentlyUsed(favorites, current?.group_id);
    if (trimmed.length < favorites.length) {
      localStorage.setItem(FAVORITE_GROUPS_KEY, JSON.stringify({ v: 1, groups: trimmed }));
    }
    return trimmed;
  }

  // First launch of the version: the network in use becomes the first favorite.
  const migrated = current?.group_id ? [toFavorite(current, true)] : [];
  localStorage.setItem(FAVORITE_GROUPS_KEY, JSON.stringify({ v: 1, groups: migrated }));
  return migrated;
}

export const useFavoriteGroupsStore = defineStore('favorite-groups', () => {
  /** In the order they were added. */
  const favorites = ref<FavoriteGroup[]>(loadFavoriteGroups());

  function save() {
    localStorage.setItem(FAVORITE_GROUPS_KEY, JSON.stringify({ v: 1, groups: favorites.value }));
  }

  function has(groupId: string): boolean {
    return favorites.value.some(favorite => favorite.groupId === groupId);
  }

  function add(group: FavoriteGroupSource) {
    if (has(group.group_id)) return;
    // Only the first favorite notifies by default, so that subscriptions do not pile up.
    favorites.value.push(toFavorite(group, favorites.value.length === 0, Date.now()));
    favorites.value = evictLeastRecentlyUsed(favorites.value, group.group_id);
    save();
  }

  function markUsed(groupId: string) {
    const favorite = favorites.value.find(item => item.groupId === groupId);
    if (!favorite) return;
    favorite.lastUsedAt = Date.now();
    save();
  }

  function remove(groupId: string) {
    favorites.value = favorites.value.filter(favorite => favorite.groupId !== groupId);
    save();
  }

  function setNotificationsEnabled(groupId: string, notificationsEnabled: boolean) {
    const favorite = favorites.value.find(item => item.groupId === groupId);
    if (!favorite || favorite.notificationsEnabled === notificationsEnabled) return;
    favorite.notificationsEnabled = notificationsEnabled;
    save();
  }

  /** Keeps a favorite's name, color and logo in step with the network as the API returns it. */
  function refresh(group: FavoriteGroupSource) {
    const index = favorites.value.findIndex(favorite => favorite.groupId === group.group_id);
    if (index < 0) return;
    const current = favorites.value[index];
    const updated = toFavorite(group, current.notificationsEnabled, current.lastUsedAt);
    if (JSON.stringify(updated) === JSON.stringify(current)) return;
    favorites.value[index] = updated;
    save();
  }

  return { favorites, has, add, remove, refresh, markUsed, setNotificationsEnabled };
});
